/**
 * Shortcut registry (items #83 + #84 Phase 2).
 *
 * Single source of truth for keyboard shortcuts shown in the Cmd-? cheat sheet.
 * Adding a new shortcut: register it here AND wire its handler wherever needed.
 * The cheat sheet auto-generates from this list - never stale.
 */

export type ShortcutContext = 'global' | 'inbox' | 'deals' | 'card-stack' | 'draft-editor';

export interface ShortcutDef {
  /** Display string like "⌘ K" or "g i" (space-separated keys). */
  keys: string;
  /** Short label shown in the cheat sheet. */
  label: string;
  /** Context group where this shortcut is active. */
  context: ShortcutContext;
  /** If true, only active when the user has opted into vim keybindings. */
  vimOnly?: boolean;
}

export const SHORTCUTS: ShortcutDef[] = [
  // Global
  { context: 'global', keys: '⌘ K',       label: 'Open command palette' },
  { context: 'global', keys: '⌘ ?',       label: 'Show this cheat sheet' },
  { context: 'global', keys: '⌘ /',       label: 'Toggle Neo chat panel' },
  { context: 'global', keys: 'd',         label: 'Open Neo dossier (party page)' },
  { context: 'global', keys: 'g i',       label: 'Go to Inbox' },
  { context: 'global', keys: 'g d',       label: 'Go to Deals' },
  { context: 'global', keys: '⌘ Shift F', label: 'Focus mode (hide nav + panes)' },
  { context: 'global', keys: '⌘ Shift S', label: 'Send screenshot to Neo' },

  // Inbox
  { context: 'inbox', keys: 'j',          label: 'Next message' },
  { context: 'inbox', keys: 'k',          label: 'Previous message' },
  { context: 'inbox', keys: 'r',          label: 'Reply' },
  { context: 'inbox', keys: 's',          label: 'Snooze message' },
  { context: 'inbox', keys: 'e',          label: 'Archive' },
  { context: 'inbox', keys: '/',          label: 'Filter list' },

  // Deals
  { context: 'deals', keys: '/',          label: 'Search deals' },
  { context: 'deals', keys: 'n',          label: 'New deal' },
  { context: 'deals', keys: 'f',          label: 'Filter' },
  { context: 'deals', keys: 'x',          label: 'Multi-select toggle' },

  // Card stack (MOAT)
  { context: 'card-stack', keys: 'j',     label: 'Approve current card' },
  { context: 'card-stack', keys: 'k',     label: 'Reject current card' },
  { context: 'card-stack', keys: 'e',     label: 'Edit current card' },
  { context: 'card-stack', keys: 'r',     label: 'Regenerate' },
  { context: 'card-stack', keys: 'Space', label: 'Skip card' },

  // Draft editor
  { context: 'draft-editor', keys: '⌘ B',     label: 'Bold' },
  { context: 'draft-editor', keys: '⌘ I',     label: 'Italic' },
  { context: 'draft-editor', keys: '⌘ U',     label: 'Underline' },
  { context: 'draft-editor', keys: '⌘ Enter', label: 'Send message' },

  // Vim opt-in (only shown when vim mode is on)
  { context: 'global', keys: 'g g',      label: 'Jump to top of list', vimOnly: true },
  { context: 'global', keys: 'Shift G',  label: 'Jump to bottom of list', vimOnly: true },
  { context: 'global', keys: 'd d',      label: 'Delete current item', vimOnly: true },
];

export const CONTEXT_LABEL: Record<ShortcutContext, string> = {
  global:        'Global',
  inbox:         'Inbox',
  deals:         'Deals',
  'card-stack':  'Batch card stack',
  'draft-editor': 'Draft editor',
};

// Vim opt-in preference helpers

const VIM_STORAGE_KEY = 'poe-vim-keys';

export function getVimEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(VIM_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setVimEnabled(enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem(VIM_STORAGE_KEY, '1');
    else localStorage.removeItem(VIM_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
